import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  CheckCircle,
  Eye,
  FileBadge,
  Hash,
  Play,
  RotateCcw,
  StopCircle,
  X,
} from "lucide-react";

import NoIndex from "../../../components/NoIndex";
import Pagination from "../../../utils/TablePagination";

import useCampaigns from "../../../services/Whatsapp/useCampaigns";
import useTemplates from "../../../services/Whatsapp/useTemplates";
import useWhatsappNumbers from "../../../services/Whatsapp/useWhatsappNumbers";
import LoadingScreen from "../../../utils/LoadingScreen";
import {
  formatDate,
  formatDateTime,
  formatTime,
} from "../../../utils/dateFormatter";

const Campaign = () => {
  const navigate = useNavigate();

  const { loading, setLoading, getCampaigns } = useCampaigns();
  const { templates, fetchVendorTemplates } = useTemplates();
  const { whatsappNumbers } = useWhatsappNumbers();

  const [singleWhatsApp, setSingleWhatsApp] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [campaignList, setCampaignList] = useState([]);

  const getCampaignCacheKey = (phoneNumberId) =>
  `whatsapp_campaigns_cache_${phoneNumberId}`;

const getTemplateCacheKey = (phoneNumberId) =>
  `whatsapp_templates_cache_${phoneNumberId}`;

  const itemsPerPage = 10;

 const handleRefresh = async () => {
  try {
    setLoading(true);

    if (!singleWhatsApp) return;

    const [campaignData] = await Promise.all([
      getCampaigns(singleWhatsApp),
      fetchVendorTemplates(singleWhatsApp),
    ]);

    const finalCampaigns = Array.isArray(campaignData) ? campaignData : [];

    setCampaignList(finalCampaigns);

    sessionStorage.setItem(
      getCampaignCacheKey(singleWhatsApp),
      JSON.stringify(finalCampaigns)
    );

    sessionStorage.setItem(
      getTemplateCacheKey(singleWhatsApp),
      JSON.stringify(templates || [])
    );
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};

  const sortedData = [...(campaignList || [])].sort(
    (a, b) => new Date(b.campaign_start) - new Date(a.campaign_start),
  );

  const filteredData = sortedData.filter((item) =>
    item.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePreview = (templateId) => {
    if (!templateId) return toast.error("Template ID not found");

    const template = templates.find(
      (t) =>
        t.name === templateId ||
        t.name?.toLowerCase() === templateId?.toLowerCase() ||
        t.id === templateId,
    );

    if (!template) {
      return toast.error("Template not found");
    }

    setPreviewTemplate(template);
  };

  const cleanBrokenText = (text = "") => {
    return (
      String(text)
        // remove invisible/control/binary characters
        .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
        // remove common broken encoded junk characters
        .replace(/[�]/g, "")
        .trim()
    );
  };

  const isMostlyReadable = (text = "") => {
    if (!text) return false;

    const readableChars =
      text.match(/[a-zA-Z0-9\s.,!?'"():;@#₹%&*/+\-_]/g) || [];
    return readableChars.length / text.length > 0.75;
  };

  const decodeBase64 = (str) => {
    if (!str) return "";

    const cleanStr = String(str).trim();

    try {
      // If it already looks like normal message text, don't decode it
      if (cleanStr.includes(" ") || cleanStr.includes(".")) {
        return cleanBrokenText(cleanStr);
      }

      const isBase64 =
        /^[A-Za-z0-9+/]+={0,2}$/.test(cleanStr) && cleanStr.length % 4 === 0;

      if (!isBase64) {
        return cleanBrokenText(cleanStr);
      }

      const binary = atob(cleanStr);
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);

      // If decoded output is garbage, show original clean text instead
      if (!isMostlyReadable(decoded)) {
        return cleanBrokenText(cleanStr);
      }

      return cleanBrokenText(decoded);
    } catch (error) {
      console.log("Decode failed:", error);
      return cleanBrokenText(str);
    }
  };

  const getTemplateParts = (template) => {
    if (!template) return {};
    const components = template.components || [];
    const header = components.find((c) => c.type === "HEADER");
    const body = components.find((c) => c.type === "BODY");
    const footer = components.find((c) => c.type === "FOOTER");
    const buttons = components.find((c) => c.type === "BUTTONS");
    return {
      header: header?.text ? decodeBase64(header.text) : null,
      headerFormat: header?.format || null,
      body: body?.text ? decodeBase64(body.text) : null,
      footer: footer?.text ? decodeBase64(footer.text) : null,
      buttons: buttons?.buttons || [],
    };
  };

  const timeStr = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const renderWhatsAppText = (text = "") => {
    const cleanText = cleanBrokenText(text);

    // Split by WhatsApp bold pattern: *text*
    const parts = cleanText.split(/(\*[^*]+\*)/g);

    return parts.map((part, index) => {
      if (part.startsWith("*") && part.endsWith("*")) {
        return (
          <strong key={index} className="font-semibold text-gray-900">
            {part.slice(1, -1)}
          </strong>
        );
      }

      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  useEffect(() => {
    if (whatsappNumbers.length > 0 && singleWhatsApp === "") {
      setSingleWhatsApp(whatsappNumbers[0].phone_no_id);
    }
  }, [whatsappNumbers]);

  useEffect(() => {
    if (singleWhatsApp) {
      fetchVendorTemplates(singleWhatsApp);
    }
  }, [singleWhatsApp]);

  useEffect(() => {
  if (!singleWhatsApp || !Array.isArray(templates)) return;

  sessionStorage.setItem(
    getTemplateCacheKey(singleWhatsApp),
    JSON.stringify(templates)
  );
}, [singleWhatsApp, templates]);

 useEffect(() => {
  if (!singleWhatsApp) return;

  const loadCampaigns = async () => {
    try {
      setLoading(true);

      const cachedCampaigns = sessionStorage.getItem(
        getCampaignCacheKey(singleWhatsApp)
      );

      if (cachedCampaigns) {
        const parsedCampaigns = JSON.parse(cachedCampaigns);
        setCampaignList(Array.isArray(parsedCampaigns) ? parsedCampaigns : []);
        return;
      }

      const data = await getCampaigns(singleWhatsApp);
      const finalCampaigns = Array.isArray(data) ? data : [];

      setCampaignList(finalCampaigns);

      sessionStorage.setItem(
        getCampaignCacheKey(singleWhatsApp),
        JSON.stringify(finalCampaigns)
      );
    } catch (err) {
      console.error(err);
      setCampaignList([]);
    } finally {
      setLoading(false);
    }
  };

  loadCampaigns();
}, [singleWhatsApp]);

  {
    loading && (
      <div className="flex justify-center py-6">
        <LoadingScreen text="Updating orders..." />
      </div>
    );
  }

  return (
    <>
      <NoIndex />
<ToastContainer/>
      <div className="p-6 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
        <>
          {/* Top Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6 montserrat-font-1">
            <div className="flex justify-between items-center mb-4 montserrat-font-1">
              <h2 className="text-xl font-semibold">Manage Campaign</h2>
              <div className="flex gap-3">
                <button
                  onClick={() =>
                    navigate("/WhatsApp/CreateCampaign", {
                      state: { startStep: 1 },
                    })
                  }
                  className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md text-sm font-medium montserrat-font-1"
                >
                  + Create New Campaign
                </button>

                <button
                  onClick={handleRefresh}
                  className="flex items-center gap-2 border rounded-md px-3 py-2 bg-blue-500 text-white montserrat-font-1"
                >
                  <RotateCcw strokeWidth={2.5} size={16} />
                  Refresh
                </button>
              </div>
            </div>

            <hr className="mb-4" />

            <div className="flex flex-col md:flex-row md:items-end gap-6 montserrat-font-1">
              <div className="flex flex-col montserrat-font-1">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number
                </label>
                <select
                  value={singleWhatsApp}
                  onChange={(e) => setSingleWhatsApp(e.target.value)}
                  className="w-full md:w-72 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select Number</option>
                  {whatsappNumbers.map((num, index) => (
                    <option key={index} value={num.phone_no_id}>
                      {num.phone_no_id} - ({num.phone_no})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col montserrat-font-1">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Search campaigns
                </label>
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full md:w-72 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
          </div>

          {/* Table */}
         <div className="bg-white rounded-lg shadow-sm border relative flex-1 overflow-hidden">
  <div className="h-full overflow-y-auto">
    <table className="min-w-full text-sm text-center border-collapse">
                <thead className="sticky top-0 z-10 bg-green-500 text-white">
                  <tr>
                    <th className="border border-gray-200 px-4 py-3">S.No.</th>
                    <th className="border border-gray-200">Campaign Name</th>
                    <th className="border border-gray-200">
                      Template/Category
                    </th>
                    <th className="border border-gray-200">Start/End Time</th>
                    <th className="border border-gray-200">Status</th>
                    <th className="border border-gray-200">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-10">
                        <LoadingScreen text="Loading campaigns..." />
                      </td>
                    </tr>
                  ) : currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-6 text-gray-500">
                        No campaigns found
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((item, index) => (
                      <tr
                        key={item.id}
                        // className={`hover:bg-gray-50 ${isRefreshing ? "opacity-60" : ""} transition-opacity`}
                      >
                        <td className="border border-gray-200 py-3">
                          {index + startIndex + 1}
                        </td>

                        <td className="border border-gray-200 px-3 py-2">
                          <div className="flex flex-col items-center gap-1">
                            {item.excel_url &&
                            item.excel_url !== "null" &&
                            item.excel_url.trim() !== "" ? (
                              <a
                                href={item.excel_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-1 text-blue-600 cursor-pointer hover:underline"
                                title="Download Excel"
                              >
                                <FileBadge className="w-4 h-4 text-green-600" />

                                <span className="text-sm font-medium">
                                  {item.campaign_name || "N/A"}
                                </span>
                              </a>
                            ) : (
                              <div className="flex items-center justify-center gap-1 text-sm font-medium text-gray-800">
                                <FileBadge className="w-4 h-4 text-green-600" />
                                <span>{item.campaign_name || "N/A"}</span>
                              </div>
                            )}

                            {/* ID Badge */}
                            <span className="inline-flex items-center gap-1 w-fit bg-green-100 text-gray-700 px-2 py-1 rounded-md text-xs font-medium">
                              <Hash className="w-3 h-3" />
                              {item.id}
                            </span>
                          </div>
                        </td>

                        <td className="px-5 py-3 text-center border border-gray-200">
                          <div className="flex flex-col items-center gap-1">
                            {/* Template Preview only on Eye click */}
                            <div className="text-sm font-semibold flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handlePreview(item.template_id)}
                                className="text-blue-600 hover:text-blue-800 cursor-pointer"
                                title="Preview Template"
                              >
                                <Eye className="w-4 h-4" />
                              </button>

                              <span className="text-gray-800">
                                {item.template_id || "N/A"}
                              </span>
                            </div>

                            {/* Category */}
                            <span className="text-sm text-gray-800">
                              {item.template_category || "N/A"}
                            </span>
                          </div>
                        </td>

                        <td className="border border-gray-200 px-3 py-2">
                          <div className="flex flex-col gap-1">
                            {/* Start Date */}
                            <div className="flex items-center gap-2">
                              <Play className="w-3 h-3 text-green-600" />
                              <span className="text-sm font-medium text-gray-800">
                                {formatDateTime(item.campaign_start, true)}
                              </span>
                            </div>

                            {/* End Date */}
                            <div className="flex items-center gap-2">
                              <StopCircle className="w-3 h-3 text-red-500" />
                              <span className="text-xs text-gray-500">
                                {formatDateTime(item.campaign_end, true)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="border border-gray-200">
                          <span className="bg-green-600 text-white text-xs px-4 py-1 rounded-full flex items-center justify-center gap-1 mx-auto w-fit">
                            <CheckCircle size={14} />
                            {item.status === "N" ? "Completed" : item.status}
                          </span>
                        </td>

                        <td className="border border-gray-200">
                          <button
                            onClick={() =>
                              navigate(
                                `/WhatsApp/Manage/CampaignView?campaignId=${item.id}&phoneId=${singleWhatsApp}`,
                              )
                            }
                          >
                            <Eye
                              className="w-5 h-5 mx-auto opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                              strokeWidth={2}
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6 montserrat-font-1">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onNextPage={() => setCurrentPage((p) => p + 1)}
              onPrevPage={() => setCurrentPage((p) => p - 1)}
              onPageClick={(page) => setCurrentPage(page)}
            />
          </div>
        </>

        {/* ── Message Preview Modal ── */}
        {previewTemplate &&
          (() => {
            const { header, headerFormat, body, footer, buttons } =
              getTemplateParts(previewTemplate);

            return (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 montserrat-font-1"
                onClick={() => setPreviewTemplate(null)}
              >
                <div
                  className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Modal Header */}
                  <div className="flex items-center justify-between border-b px-5 py-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        Message Preview
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        WhatsApp template preview
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setPreviewTemplate(null)}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="px-5 py-5 bg-gray-50">
                    {/* WhatsApp Background */}
                    <div className="rounded-2xl bg-[#e7f5e9] p-4">
                      {/* WhatsApp Bubble */}
                      <div className="rounded-xl bg-white px-4 py-3 shadow-sm border border-gray-100">
                        {/* Header Text */}
                        {header && headerFormat === "TEXT" && (
                          <p className="mb-3 text-sm font-semibold leading-6 text-gray-900 whitespace-pre-wrap">
                            {renderWhatsAppText(header)}
                          </p>
                        )}

                        {/* Header Image */}
                        {header && headerFormat === "IMAGE" && (
                          <img
                            src={header}
                            alt="Template header"
                            className="mb-3 max-h-44 w-full rounded-lg object-cover"
                          />
                        )}

                        {/* Body */}
                        {body && (
                          <p className="text-sm leading-6 text-gray-800 whitespace-pre-wrap">
                            {renderWhatsAppText(body)}
                          </p>
                        )}

                        {/* Footer + Time */}
                        <div className="mt-4 flex items-end justify-between gap-3">
                          {footer ? (
                            <p className="text-xs leading-5 text-gray-400 whitespace-pre-wrap">
                              {renderWhatsAppText(footer)}
                            </p>
                          ) : (
                            <span />
                          )}

                          <p className="shrink-0 text-[11px] text-gray-400">
                            {timeStr}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {buttons.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {buttons.map((btn, i) => (
                            <button
                              key={i}
                              type="button"
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-center text-sm font-medium text-green-600 shadow-sm hover:bg-gray-50"
                            >
                              {btn.text}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="flex justify-end gap-3 border-t bg-white px-5 py-4">
                    <button
                      type="button"
                      onClick={() => setPreviewTemplate(null)}
                      className="rounded-md bg-gray-800 px-5 py-2 text-sm font-medium text-white hover:bg-gray-900"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
      </div>
    </>
  );
};

export default Campaign;
